// hooks/useOrganizationBucketSync.js
// Syncs organization AWS bucket configuration from backend

import { useEffect, useCallback } from 'react';
import { useUser } from '../../../features/auth/hooks/useUser';
import { OrganizationBucketService } from '../services/OrganizationBucketService';


/**
 * Hook to sync organization AWS bucket configuration from backend
 * Calls GET /api/v1.0/organization/{orgId}/aws-config
 * This ensures the frontend always has the latest custom bucket settings
 */
export const useOrganizationBucketSync = () => {
  const { userData, setUserData } = useUser();

  /**
   * Fetch and sync organization AWS bucket configuration
   */
  const syncBucketConfig = useCallback(async () => {
    // CRITICAL: Only sync if user is actually authenticated
    // Check for essential auth fields to prevent pre-login API calls
    if (!userData || !userData.userId || !userData.accessToken || !userData.tenantId || !userData.divisionId) {
      return;
    }

    // Skip if we already have custom bucket config
    // Check if customBuckets flag is set (even if false) - this means we've already fetched the config
    const alreadyHasConfig = (userData.customBuckets === true || userData.customBuckets === false);

    if (alreadyHasConfig) {
      console.log('✅ Bucket config already synced:', {
        customBuckets: userData.customBuckets,
        projectsBucketName: userData.projectsBucketName
      });
      return;
    }

    console.log('🔄 Syncing organization AWS bucket config for:', userData.tenantId, 'division:', userData.divisionId);

    try {
      // Fetch AWS config from your EXISTING endpoint
      const result = await OrganizationBucketService.fetchOrganizationConfig(
        userData.tenantId,
        userData.divisionId
      );

      if (result.success && result.data) {
        // Merge AWS bucket config into user data
        const updatedUserData = OrganizationBucketService.mergeOrgBucketConfig(
          userData,
          result.data
        );

        // Update user data (this will also update localStorage)
        setUserData(updatedUserData);

        console.log('✅ Organization AWS bucket config synced:', {
          customBuckets: updatedUserData.customBuckets,
          projectsBucketName: updatedUserData.projectsBucketName
        });
      } else {
        console.warn('⚠️ Failed to sync bucket config:', result.error);
      }
    } catch (error) {
      console.error('❌ Error syncing bucket config:', error);
    }
  }, [userData?.tenantId, userData?.divisionId, userData?.customBuckets, userData?.projectsBucketName, setUserData]);

  /**
   * Sync on mount and when organization changes
   * Only runs if user is authenticated
   */
  useEffect(() => {
    // Only sync if user is authenticated
    if (userData?.userId && userData?.accessToken && userData?.tenantId && userData?.divisionId) {
      syncBucketConfig();
    }
  }, [userData?.userId, userData?.accessToken, userData?.tenantId, userData?.divisionId, syncBucketConfig]);

  return {
    syncBucketConfig,
    isUsingCustomBucket: userData?.customBuckets === true,
    projectsBucketName: userData?.projectsBucketName
  };
};